import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { userApi } from './userApi'

export interface PlanDefinition {
  name: 'free' | 'premium' | 'pro'
  displayName: string
  monthlyPrice: number
  annualPrice: number
  features: string[]
}

export interface CurrentSubscription {
  plan: 'free' | 'premium' | 'pro'
  displayName: string
  monthlyPrice: number
  annualPrice: number
  features: string[]
  canDowngrade: boolean
  billingIntegrated: boolean
}

export interface SelectPlanResponse {
  success: boolean
  plan: 'free' | 'premium' | 'pro'
  waitlisted?: boolean
  message: string
}

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/subscriptions',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['CurrentSubscription'],
  endpoints: (builder) => ({
    // Get available plans (authenticated)
    getPlans: builder.query<PlanDefinition[], void>({
      query: () => '/plans',
      transformResponse: (response: { success: boolean; data: PlanDefinition[] }) => response.data,
    }),

    // Get current user subscription
    getCurrentSubscription: builder.query<CurrentSubscription, void>({
      query: () => '/current',
      transformResponse: (response: { success: boolean; data: CurrentSubscription }) => response.data,
      providesTags: ['CurrentSubscription'],
    }),

    // Select a plan
    selectPlan: builder.mutation<SelectPlanResponse, { plan: 'free' | 'premium' | 'pro' }>({
      query: (body) => ({
        url: '/select',
        method: 'POST',
        body,
      }),
      transformResponse: (response: SelectPlanResponse) => response,
      invalidatesTags: ['CurrentSubscription'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (!data.waitlisted) {
            dispatch(userApi.util.invalidateTags(['User']))
          }
        } catch {
          // ignore — mutation error handled by caller
        }
      },
    }),
  }),
})

export const {
  useGetPlansQuery,
  useGetCurrentSubscriptionQuery,
  useSelectPlanMutation,
} = subscriptionApi
